import React from 'react';
import { FormattedMessage } from 'react-intl';
import css from '../views/panes/InfoPane.css';
import { getLocalizedDate, getLocalizedTime } from './DateUtils';
import { getWeekdaySpan } from './WeekdayUtils';
/**
 * Used for comparison to ensure hours are properly sorted.
 * Used for when an opening spans multiple days; this will fully replace an opening or closing
 * for a given day
 */
export var NEXT_DAY_FULL_WRAPAROUND = '-';
/**
 * Used for when an opening spans part of a night (as determined by {@code OVERNIGHT_THRESHOLD}),
 * such as if something is open until 1AM.  Instead of a full replacement, this is appended
 * to an end time (will be parsed out in rendering).
 */
export var NEXT_DAY_OVERNIGHT = '*';
/**
 * The time at which a closure is considered the next day, rather than the current day
 * This will be compared lexicographically.
 *
 * For example, with threshold of "04:00", if an opening is Monday 13:00 - Tuesday 02:00
 * then it will show:
 *   Monday: 13:00 to 02:00* (with * denoting next day)
 *   Tuesday: closed
 *
 * If it was Monday 13:00 - Tuesday 06:00, then:
 *   Monday: 13:00 to -- (with -- denoting it does not close on Monday)
 *   Tuesday: -- to 06:00 (with -- denoting it was already open)
 *
 * The idea of this is to reduce confusion with instances where, for example, a library
 * is frequently open until 1am the next morning.
 */
export var OVERNIGHT_THRESHOLD = '04:00';
/**
 * Used for sorting a series of opening tuples for a day.  These tuples may contain
 * {@code NEXT_DAY_FULL_WRAPAROUND} as appropriate
 * @param a A tuple of open/close times
 * @param b A tuple of open/close times
 * @return -1, 0, or 1 depending on comparison result
 */
export function openingSorter(a, b) {
    // A wrapped from previous day or B wraps to next day
    if (a[0] === NEXT_DAY_FULL_WRAPAROUND || b[1] === NEXT_DAY_FULL_WRAPAROUND) {
        return -1;
    }
    // B wrapped from previous day or A wraps to next day
    if (b[0] === NEXT_DAY_FULL_WRAPAROUND || a[1] === NEXT_DAY_FULL_WRAPAROUND) {
        return 1;
    }
    return a[0].localeCompare(b[0]);
}
/**
 * Split an array of openings into a record of weekdays mapped to arrays of
 * `OpenCloseTimeTuple`s
 */
export function splitOpeningsIntoDays(openings) {
    var hours = {
        MONDAY: [],
        TUESDAY: [],
        WEDNESDAY: [],
        THURSDAY: [],
        FRIDAY: [],
        SATURDAY: [],
        SUNDAY: [],
    };
    openings.forEach(function (opening) {
        var startTime = opening.startTime;
        var endTime = opening.endTime;
        var span = getWeekdaySpan(opening);
        // if the closing time should be considered overnight on the previous day,
        // rather than a full additional day of opening
        if (span.length > 1 && endTime <= OVERNIGHT_THRESHOLD) {
            span.pop();
            endTime += NEXT_DAY_OVERNIGHT; // append to denote next day
        }
        span.forEach(function (day, i) {
            var bounds = [
                NEXT_DAY_FULL_WRAPAROUND,
                NEXT_DAY_FULL_WRAPAROUND,
            ];
            if (i === 0) {
                bounds[0] = startTime;
            }
            if (i === span.length - 1) {
                bounds[1] = endTime;
            }
            hours[day].push(bounds);
        });
    });
    Object.keys(hours).forEach(function (day) {
        hours[day].sort(openingSorter);
    });
    return hours;
}
export function get247Rows(intl, localeWeekdays) {
    return localeWeekdays.map(function (weekday, i) { return ({
        day: weekday.long,
        startTime: (React.createElement("p", { key: i, title: intl.formatMessage({ id: 'ui-calendar.infoPane.247HelpText' }) }, "\u2013")),
        endTime: (React.createElement("p", { key: i, title: intl.formatMessage({ id: 'ui-calendar.infoPane.247HelpText' }) }, "\u2013")),
    }); });
}
/** Generate rows to display, based on hours from `splitOpeningsIntoDays` */
export function generateDisplayRows(intl, localeWeekdays, hours) {
    return localeWeekdays.map(function (weekday) {
        var tuples = hours[weekday.weekday];
        var times = {
            startTime: [],
            endTime: [],
        };
        if (tuples.length === 0) {
            times.startTime.push(React.createElement("p", { className: css.closed, key: "closed" },
                React.createElement(FormattedMessage, { id: "ui-calendar.infoPane.display.closed" })));
            times.endTime.push(React.createElement("div", { key: "closed" },
                "\u00A0",
                React.createElement("p", { className: "sr-only" },
                    React.createElement(FormattedMessage, { id: "ui-calendar.infoPane.display.closed" }))));
        }
        tuples.forEach(function (_a, i) {
            var open = _a[0], close = _a[1];
            if (open === NEXT_DAY_FULL_WRAPAROUND) {
                times.startTime.push(React.createElement("p", { key: i, title: intl.formatMessage({
                        id: 'ui-calendar.infoPane.display.wrappedOpening',
                    }) }, "\u2013"));
            }
            else {
                times.startTime.push(React.createElement("p", { key: i }, getLocalizedTime(intl, open)));
            }
            if (close === NEXT_DAY_FULL_WRAPAROUND) {
                times.endTime.push(React.createElement("p", { key: i, title: intl.formatMessage({
                        id: 'ui-calendar.infoPane.display.wrappedClosed',
                    }) }, "\u2013"));
            }
            else if (close.endsWith(NEXT_DAY_OVERNIGHT)) {
                times.endTime.push(React.createElement("p", { key: i, title: intl.formatMessage({
                        id: 'ui-calendar.infoPane.display.closesAfterMidnight',
                    }) },
                    React.createElement(FormattedMessage, { id: "ui-calendar.infoPane.display.closesAfterMidnightTime", values: {
                            time: getLocalizedTime(intl, close.replace(NEXT_DAY_OVERNIGHT, '').trim()),
                        } })));
            }
            else {
                times.endTime.push(React.createElement("p", { key: i }, getLocalizedTime(intl, close)));
            }
        });
        return {
            day: weekday.long,
            startTime: React.createElement(React.Fragment, null, times.startTime),
            endTime: React.createElement(React.Fragment, null, times.endTime),
        };
    });
}
/** Display rows for exceptional information */
export function generateExceptionalOpeningRows(intl, exceptions) {
    return exceptions.map(function (exception) {
        var times = {
            start: [],
            end: [],
        };
        exception.openings.forEach(function (_a, i) {
            var startDate = _a.startDate, startTime = _a.startTime, endDate = _a.endDate, endTime = _a.endTime;
            times.start.push(React.createElement("p", { key: i },
                getLocalizedDate(intl, startDate),
                React.createElement("br", null),
                getLocalizedTime(intl, startTime)));
            times.end.push(React.createElement("p", { key: i },
                getLocalizedDate(intl, endDate),
                React.createElement("br", null),
                getLocalizedTime(intl, endTime)));
        });
        return {
            name: exception.name,
            start: React.createElement(React.Fragment, null, times.start),
            end: React.createElement(React.Fragment, null, times.end),
        };
    });
}
/**
 * Used to detect when help text for * should be rendered.
 */
export function containsNextDayOvernight(hours) {
    for (var _i = 0, _a = Object.values(hours); _i < _a.length; _i++) {
        var tuples = _a[_i];
        for (var _b = 0, tuples_1 = tuples; _b < tuples_1.length; _b++) {
            var tuple = tuples_1[_b];
            if (tuple[0].endsWith(NEXT_DAY_OVERNIGHT) ||
                tuple[1].endsWith(NEXT_DAY_OVERNIGHT)) {
                return true;
            }
        }
    }
    return false;
}
/**
 * Used to detect when help text for -- should be rendered.
 */
export function containsFullOvernightSpans(hours) {
    for (var _i = 0, _a = Object.values(hours); _i < _a.length; _i++) {
        var tuples = _a[_i];
        for (var _b = 0, tuples_2 = tuples; _b < tuples_2.length; _b++) {
            var tuple = tuples_2[_b];
            if (tuple[0] === NEXT_DAY_FULL_WRAPAROUND ||
                tuple[1] === NEXT_DAY_FULL_WRAPAROUND) {
                return true;
            }
        }
    }
    return false;
}
